import moment from "moment";
import constants from "../core/constants";
import countries from "../core/countries";
import {
  each,
  isObject,
  isArray,
  compact,
  join,
  toNumber,
  isNil,
  clone,
} from "lodash/fp";

export function assignParams(currentParams, newParams) {
  const options = Object.assign({}, currentParams);
  for (const paramKey in newParams) {
    if (newParams[paramKey] === null) {
      options[paramKey] = null;
    } else if (typeof newParams[paramKey] !== "undefined") {
      options[paramKey] = newParams[paramKey];
    }
  }

  return options;
}

/**
 * @param {Object} obj
 * @param {Boolean} noDisable
 */
export function toObject(obj, noDisable = false) {
  const clonedObj = clone(obj);
  for (const prop in clonedObj) {
    if (Object.prototype.hasOwnProperty.call(clonedObj, prop)) {
      // Type Guarding for-in
      if (isObject(clonedObj[prop])) {
        clonedObj[prop].key = prop;
      } else {
        const newItem = {};

        newItem.key = prop;
        newItem.value = clonedObj[prop];
        clonedObj[prop] = newItem;
      }
      if (typeof clonedObj[prop].isDisabled === "undefined") {
        if (!noDisable) {
          clonedObj[prop].isDisabled = true;
        } else {
          clonedObj[prop].isDisabled = false;
        }
      }
    }
  }

  return clonedObj;
}

export function humanize(str) {
  const frags = str.split("_");
  for (let i = 0; i < frags.length; i++) {
    frags[i] = capitalize(frags[i]);
  }
  return frags.join(" ");
}
export function getState(base, params) {
  const combineUrl = (state, param) =>
    state.length === 1 ? `${state}${param}` : `${state}&${param}`;

  return base + "/" + params.reduce(combineUrl, "?");
}

export function parseBackUrl(url) {
  const params = {
    back: null,
  };
  const getParamsObj = (param) => {
    const keyValue = param.split("=");
    const key = keyValue[0];
    const value = keyValue[1];
    const paramObj = {};

    paramObj[key] = value;

    if (params[key]) {
      if (isArray(params[key])) {
        params[key] = [].concat(...params[key], [value]);
      } else {
        params[key] = [params[key], value];
      }
    } else {
      Object.assign(params, paramObj);
    }
  };
  const splitQuery = url.split("?");
  const base = splitQuery[0];
  const stringParams = splitQuery[1] ? splitQuery[1].split("&") : [];
  each(getParamsObj)(stringParams);

  return {
    base,
    params,
  };
}

export function toMilliseconds(time) {
  return toNumber(time) * 1000 * 60 * 60;
}

export function fromMilliseconds(time) {
  return toNumber(time) / 1000 / 60 / 60;
}

export function getTime(time) {
  if (!(time instanceof Date)) {
    time = new Date(time);
  }
  const hours = time.getHours() * 60 * 60 * 1000;
  const mins = time.getMinutes() * 60 * 1000;
  const seconds = time.getSeconds() * 1000;
  const millSeconds = time.getMilliseconds();

  return hours + mins + seconds + millSeconds;
}

export function isDateAfter(date, now = new Date()) {
  const expectedDate = moment(date, constants.dateTimeFormat);
  const todayDate = moment(now, constants.dateTimeFormat);

  return moment(todayDate).diff(expectedDate, "minutes") > 0;
}

export function parsePrice(price) {
  return (Number(price) / constants.currencyBy).toFixed(2);
}

export function composePrice(price) {
  return Number(price) * constants.currencyBy;
}

export function timeDiff(time1, time2) {
  let isInTime;
  const seconds = moment(time1).diff(moment(time2), "seconds");
  const millSeconds = Math.abs(seconds) * 1000;
  const hours = moment.duration(millSeconds).hours();
  const minutes = moment.duration(millSeconds).minutes();
  const isAfter = seconds > 0;
  const message = compact([
    hours ? hours + "h" : "",
    minutes ? minutes + "m" : "",
  ]);
  const time = {
    seconds: Math.abs(seconds),
  };

  if (minutes) time.minutes = minutes;
  if (hours) time.hours = hours;
  if (message.length > 0) time.message = join(" ", message);
  if (message.length === 0) isInTime = true;

  return {
    time,
    isAfter,
    isInTime,
  };
}

// This function adopted from:
// https://stackoverflow.com/questions/4068373/center-a-popup-window-on-screen
// with additional things
export function popupDialog(url, title, w, h) {
  // Fixes dual-screen position                         Most browsers      Firefox
  const dualScreenTop = isNil(window.screenTop)
    ? window.screenTop
    : window.screenY;

  const width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
    ? document.documentElement.clientWidth
    : screen.width;
  const height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
    ? document.documentElement.clientHeight
    : screen.height;

  const systemZoom = width / window.screen.availWidth;
  const top = (height - h) / systemZoom + dualScreenTop;
  const newWindow = window.open(
    url,
    "_blank",
    "scrollbars=yes, width=" +
      w / systemZoom +
      ", height=" +
      h / systemZoom +
      ", top=" +
      top +
      ", left=" +
      0
  );

  // Puts focus on the newWindow
  newWindow.blur();
  window.focus();
}

// For the sake of fu**ing purecloud
export function getUriHash(params) {
  location.hash
    .substring(1)
    .split("&")
    .forEach((inst) => {
      const temp = inst.split("=");
      params[temp[0]] = temp[1];
    });
  return params;
}

export function saveCanvas(uri, filename) {
  const link = document.createElement("a");

  if (typeof link.download === "string") {
    link.href = uri;
    link.download = filename;

    // Firefox requires the link to be in the body
    document.body.appendChild(link);

    // Simulate click
    link.click();

    // Remove the link when done
    document.body.removeChild(link);
  } else {
    window.open(uri);
  }
}

export function capitalize(s) {
  if (typeof s !== "string") return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const groupByProp = (arr, prop) =>
  arr.reduce((acc, curr) => {
    if (!Object.prototype.hasOwnProperty.call(acc, [curr[prop]])) {
      acc[curr[prop]] = [];
    }
    acc[curr[prop]].push(curr);
    return acc;
  }, {});

/**
 * Return the sum of numeric values of a specific key in a list of objects
 * @param {{[prop: string]: number}[]} arr
 * @param {string} prop
 * @returns {number}
 */
export function sumByProp(arr, prop) {
  return arr.reduce((sum, currentObject) => {
    sum += currentObject[prop] || 0;

    return sum;
  }, 0);
}

export const getCurrentDate = () => {
  return moment().format(constants.dateFormat);
};

export const formatKey = (key) => {
  return key
    .split("_")
    .map((word) => `${word[0].toUpperCase()}${word.slice(1)}`)
    .join(" ");
};

export const toPhoneObject = (value, { country }) => {
  if (isNil(country)) throw new Error("No country id provided!");

  const countryPhoneDetails = countries[country];
  const {
    country_code: countryCode,
    region_code: regionCode,
  } = countryPhoneDetails;
  return {
    region_code: regionCode,
    country_code: countryCode,
    national_number: String(value),
  };
};

export const multiplyFloatingPointByCurrency = (number) => {
  return parseFloat((number * constants.currencyBy).toFixed(0));
};

// Returns the provided  milliseconds in AM-PM text format from start of day
export const formatHoursInMillisecondsFromStartOfDay = (hour) => {
  return moment().startOf("day").add(hour, "milliseconds").format("hh:mm A");
};
// 24 hrs in a form of milliseconds
export const convertHoursToMilliseconds = () => {
  const buckets = new Array(25)
    .fill(1)
    .map((cell, i) => cell * i * constants.fromHourToMilli);

  return buckets;
};

// Returns an array of objects where each object has 3 attributes
// title: 11AM - 12AM
// from: 11AM (in ms)
// to: 12AM (in ms)
// increasing 1 hour per cell
export const formatTwentyFourHourTimeFilter = () => {
  const twentyFourHourMilliseconds = convertHoursToMilliseconds();
  const twentyFourHourFormattedObject = twentyFourHourMilliseconds.map(
    (hour, i) => {
      if (i < twentyFourHourMilliseconds.length) {
        return {
          title: `${formatHoursInMillisecondsFromStartOfDay(
            hour
          )} - ${formatHoursInMillisecondsFromStartOfDay(
            twentyFourHourMilliseconds[i + 1]
          )}`,
          duration: {
            from: hour,
            to: twentyFourHourMilliseconds[i + 1],
          },
        };
      }
      return null;
    }
  );
  // Removing the last index
  twentyFourHourFormattedObject.pop();
  return twentyFourHourFormattedObject;
};

export const formatDate = (date) => {
  const formattedDate = moment(date).format("YYYY-MM-DD");
  return formattedDate;
};

export const formatTimeZone = (time) => {
  return (
    Number(moment(time).format(constants.unixFormat)) - constants.twoHoursTime
  );
};

export const convertAmPmTimeToMinutes = (time) => {
  const mmt = moment(time);
  const mmtMidnight = mmt.clone().startOf("day");
  const diffMinutes = mmt.diff(mmtMidnight, "minutes");

  return diffMinutes;
};

export const formatTime = (time) => {
  time = moment(time).clone();

  const hours = Number(time.hours()) * 60 * 60 * 1000;
  const mins = Number(time.minutes()) * 60 * 1000;
  const seconds = Number(time.seconds()) * 1000;
  const millSeconds = Number(time.milliseconds());

  return hours + mins + seconds + millSeconds;
};

export const formatPolylineForGoogleMaps = (path) => {
  return path.map((point) => [point[1], point[0]]);
};

export const extractEnvNameAndAppendToMarketplace = () => {
  const marketplaceURL = constants.marketplaceURL;
  if (process.env.NODE_ENV === "development") {
    const API_URL = process.env.API_URL || process.env.BUILD_API_URL;
    const cluster = API_URL.match(/(?<=api)(.*)(?=swvl)/gm);
    const devMarketplaceURL = `https://marketplace-dashboard${cluster}swvl.io/`;
    const envName = API_URL.substring(
      API_URL.indexOf("/") + 2,
      API_URL.indexOf("-api")
    );
    return `${devMarketplaceURL.slice(
      0,
      API_URL.indexOf("/") + 2
    )}${envName}-${devMarketplaceURL.slice(API_URL.indexOf("/") + 2)}`;
  }
  return marketplaceURL;
};



// WEBPACK FOOTER //
// ./src/core/utils.js